// src/pages/TradingData/compositeUtils.js
// Pure functions: date helpers, feature computation, Excel parse/export,
// composite config builder, signal pool helpers.

import * as XLSX from 'xlsx';
import { DAY_LABELS } from './compositeConstants';

// ─────────────────────────────────────────────────────────────
// Date / time helpers
// ─────────────────────────────────────────────────────────────
export function parseDate(str) {
  if (!str) return null;
  try { return new Date(str.includes('T') ? str : str + 'T00:00:00'); }
  catch { return null; }
}

export function minutesDiff(a, b) {
  const da = parseDate(a), db = parseDate(b);
  if (!da || !db) return null;
  return (db - da) / 60000;
}

export function getBacktestCutoff(weeksAgo = 3) {
  const d = new Date();
  d.setDate(d.getDate() - weeksAgo * 7);
  return d.toISOString().slice(0, 10);
}

export function today() {
  return new Date().toISOString().slice(0, 10);
}

export function defaultBacktestStart() {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toISOString().slice(0, 10);
}

/** Format a datetime string to "YYYY-MM-DD HH:mm:ss" for Excel output. */
export function fmtDatetime(str) {
  if (!str) return '';
  const d = parseDate(str);
  if (!d || isNaN(d)) return str;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Return the English weekday name for a date string. */
export function weekdayName(str) {
  const d = parseDate(str);
  if (!d || isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { weekday: 'long' });
}

// ─────────────────────────────────────────────────────────────
// Feature computation from raw trades
// ─────────────────────────────────────────────────────────────
export function computeFeatures(trades, windowMinutes = 60) {
  if (!trades || trades.length === 0) return [];
  const completed = trades.filter(t => t.pnl !== null && t.pnl !== undefined && t.pnl !== '');
  if (completed.length === 0) return [];

  const totalWins = completed.filter(t => Number(t.pnl) > 0).length;
  const baseRate = totalWins / completed.length;
  const features = [];

  // ── Companion presence features ──────────────────────────
  const companions = Object.keys(completed[0]?.companions || {});
  for (const comp of companions) {
    const tradesWithFeature = completed.filter(t => {
      const ts = t.companions?.[comp];
      if (!ts) return false;
      const diff = minutesDiff(ts, t.entry_time);
      return diff !== null && diff >= 0 && diff <= windowMinutes;
    });
    const tradesWithout = completed.filter(t => {
      const ts = t.companions?.[comp];
      if (!ts) return true;
      const diff = minutesDiff(ts, t.entry_time);
      return diff === null || diff < 0 || diff > windowMinutes;
    });
    const winsWithFeature = tradesWithFeature.filter(t => Number(t.pnl) > 0).length;
    const rateTrue = tradesWithFeature.length > 0 ? winsWithFeature / tradesWithFeature.length : 0;
    features.push({
      id: `companion_${comp}_before`, label: `${comp} before entry (≤${windowMinutes}m)`,
      category: 'companion', signal: comp, conditionType: 'time_bound',
      rateTrue, rateBase: baseRate,
      nTrue: tradesWithFeature.length, nFalse: tradesWithout.length,
      delta: rateTrue - baseRate,
    });

    const afterTrades = completed.filter(t => {
      const ts = t.companions?.[comp];
      if (!ts) return false;
      const diff = minutesDiff(t.entry_time, ts);
      return diff !== null && diff >= 0 && diff <= windowMinutes;
    });
    const afterWins = afterTrades.filter(t => Number(t.pnl) > 0).length;
    const rateAfter = afterTrades.length > 0 ? afterWins / afterTrades.length : 0;
    features.push({
      id: `companion_${comp}_after`, label: `${comp} after entry (≤${windowMinutes}m)`,
      category: 'companion', signal: comp, conditionType: 'time_bound',
      rateTrue: rateAfter, rateBase: baseRate,
      nTrue: afterTrades.length, nFalse: completed.length - afterTrades.length,
      delta: rateAfter - baseRate,
    });
  }

  // ── Day of week features ──────────────────────────────────
  for (const day of DAY_LABELS) {
    const withDay = completed.filter(t => {
      const d = parseDate(t.date || t.entry_time);
      return d && d.toLocaleDateString('en-US', { weekday: 'long' }) === day;
    });
    const withWins = withDay.filter(t => Number(t.pnl) > 0).length;
    const rateDay = withDay.length > 0 ? withWins / withDay.length : 0;
    features.push({
      id: `dow_${day.toLowerCase()}`, label: `Day = ${day}`,
      category: 'temporal', conditionType: 'active_days',
      rateTrue: rateDay, rateBase: baseRate,
      nTrue: withDay.length, nFalse: completed.length - withDay.length,
      delta: rateDay - baseRate,
    });
  }

  // ── Entry hour features ───────────────────────────────────
  for (let hr = 0; hr <= 23; hr++) {
    const withHr = completed.filter(t => {
      const d = parseDate(t.entry_time);
      return d && d.getHours() === hr;
    });
    if (withHr.length === 0) continue;
    const hrWins = withHr.filter(t => Number(t.pnl) > 0).length;
    const rateHr = hrWins / withHr.length;
    const hrLabel = hr === 0 ? '12am' : hr < 12 ? `${hr}am` : hr === 12 ? '12pm' : `${hr - 12}pm`;
    features.push({
      id: `hour_${hr}`, label: `Entry hour = ${hrLabel}`,
      category: 'temporal', conditionType: 'time_restriction',
      rateTrue: rateHr, rateBase: baseRate,
      nTrue: withHr.length, nFalse: completed.length - withHr.length,
      delta: rateHr - baseRate,
    });
  }

  // ── Companion count features ──────────────────────────────
  for (let cnt = 1; cnt <= companions.length; cnt++) {
    const withCnt = completed.filter(t => {
      const n = companions.filter(c => {
        const ts = t.companions?.[c];
        if (!ts) return false;
        const diff = minutesDiff(ts, t.entry_time);
        return diff !== null && diff >= 0 && diff <= windowMinutes;
      }).length;
      return n >= cnt;
    });
    const cntWins = withCnt.filter(t => Number(t.pnl) > 0).length;
    const rateCnt = withCnt.length > 0 ? cntWins / withCnt.length : 0;
    features.push({
      id: `companions_gte_${cnt}`, label: `≥${cnt} companion${cnt > 1 ? 's' : ''} present`,
      category: 'compound', conditionType: 'or_group',
      rateTrue: rateCnt, rateBase: baseRate,
      nTrue: withCnt.length, nFalse: completed.length - withCnt.length,
      delta: rateCnt - baseRate,
    });
  }

  return features.sort((a, b) => b.delta - a.delta);
}

// ─────────────────────────────────────────────────────────────
// Excel parser — reads the Sample_Data format
// ─────────────────────────────────────────────────────────────
export function parseExcelTrades(buffer) {
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
  if (rows.length < 2) return [];

  const headers = rows[0];
  const compCols = [];
  const companionPattern = /^([A-Z_0-9]+)\s*\(/;
  headers.forEach((h, i) => {
    if (typeof h === 'string') {
      const m = h.match(companionPattern);
      if (m && !['Entry', 'Exit', 'Duration'].includes(m[1])) {
        compCols.push({ idx: i, name: m[1] });
      }
    }
  });

  const col = (name) => headers.findIndex(h => typeof h === 'string' && h === name);
  const entryIdx = col('Entry');
  const entryDollarIdx = col('Entry $');
  const exitIdx = col('Exit');
  const exitDollarIdx = col('Exit $');
  const pnlIdx = col('P&L');
  const pnlPctIdx = col('P&L %');
  const durIdx = col('Duration');
  const dateIdx = col('Date');
  const tradeIdx = col('Trade');
  const daysIdx = col('Days');
  const tdayDiffIdx = col('TDay Diff');
  const avgIdx = col('Average');
  const ddIdx = col('Drawdown');
  const lpIdx = col('Lowest Point');
  const ddTimeIdx = col('Drawdown Time');
  const ruIdx = col('Run-up');
  const hpIdx = col('Highest Point');
  const hpTimeIdx = col('Highest Point Time');

  return rows.slice(1).map((row, i) => {
    const companions = {};
    compCols.forEach(c => {
      const val = row[c.idx];
      companions[c.name] = (val && val !== '' && val !== '—') ? String(val) : null;
    });
    return {
      trade_number: i + 1,
      direction: tradeIdx >= 0 ? row[tradeIdx] || 'LONG' : 'LONG',
      date: dateIdx >= 0 && row[dateIdx] ? String(row[dateIdx]) : null,
      entry_time: entryIdx >= 0 ? String(row[entryIdx] || '') : null,
      entry_price: entryDollarIdx >= 0 ? row[entryDollarIdx] ?? null : null,
      exit_time: exitIdx >= 0 ? String(row[exitIdx] || '') : null,
      exit_price: exitDollarIdx >= 0 ? row[exitDollarIdx] ?? null : null,
      duration: durIdx >= 0 ? row[durIdx] ?? null : null,
      pnl: pnlIdx >= 0 && row[pnlIdx] !== undefined && row[pnlIdx] !== '' ? Number(row[pnlIdx]) : null,
      pnl_pct: pnlPctIdx >= 0 && row[pnlPctIdx] !== undefined && row[pnlPctIdx] !== '' ? Number(row[pnlPctIdx]) : null,
      days: daysIdx >= 0 ? row[daysIdx] ?? null : null,
      tday_diff: tdayDiffIdx >= 0 ? row[tdayDiffIdx] ?? null : null,
      average: avgIdx >= 0 ? row[avgIdx] ?? null : null,
      drawdown: ddIdx >= 0 ? row[ddIdx] ?? null : null,
      lowest_point: lpIdx >= 0 ? row[lpIdx] ?? null : null,
      drawdown_time: ddTimeIdx >= 0 ? String(row[ddTimeIdx] || '') : null,
      runup: ruIdx >= 0 ? row[ruIdx] ?? null : null,
      highest_point: hpIdx >= 0 ? row[hpIdx] ?? null : null,
      highest_point_time: hpTimeIdx >= 0 ? String(row[hpTimeIdx] || '') : null,
      companions,
    };
  });
}

// ─────────────────────────────────────────────────────────────
// Excel export — matches Sample_Data.xlsx format exactly
// ─────────────────────────────────────────────────────────────
export function downloadTradesAsExcel(trades, { asset, timeframe, session, triggerSignal, companionKeys: overrideKeys }) {
  if (!trades || trades.length === 0) return;

  const companionKeys = overrideKeys || Object.keys(trades[0]?.companions || {});

  const headers = [
    '#', 'Trade', 'Date',
    ...companionKeys.map(c => `${c} (${timeframe} · ${asset} · ${session})`),
    'Entry', 'Entry $', 'Exit', 'Exit $', 'Duration',
    'P&L', 'P&L %',
    'Days', 'TDay Diff', 'Average',
    'Drawdown', 'Lowest Point', 'Drawdown Time',
    'Run-up', 'Highest Point', 'Highest Point Time',
  ];

  const rows = trades.map(t => [
    t.trade_number,
    t.direction,
    t.date || '',
    ...companionKeys.map(c => t.companions?.[c] ? fmtDatetime(t.companions[c]) : ''),
    t.entry_time ? fmtDatetime(t.entry_time) : '',
    t.entry_price ?? '',
    t.exit_time ? fmtDatetime(t.exit_time) : '',
    t.exit_price ?? '',
    t.duration || '',
    t.pnl ?? '',
    t.pnl_pct ?? '',
    t.days || weekdayName(t.date || t.entry_time),
    t.tday_diff ?? 0,
    t.average ?? '',
    t.drawdown ?? '',
    t.lowest_point ?? '',
    t.drawdown_time ? fmtDatetime(t.drawdown_time) : '',
    t.runup ?? '',
    t.highest_point ?? '',
    t.highest_point_time ? fmtDatetime(t.highest_point_time) : '',
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, `composite_raw_${asset}_${triggerSignal || 'trades'}_${today()}.xlsx`);
}

// ─────────────────────────────────────────────────────────────
// Normalize a backend trade → unified UI shape
// ─────────────────────────────────────────────────────────────
export function mapBackendTrade(t, i, direction) {
  const companionToString = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'string') return v;
    if (typeof v === 'object') return v.time || v.value || null;
    return String(v);
  };

  const entryDate = t.entry_time ? String(t.entry_time).slice(0, 10) : null;
  const exc = t.excursion || {};

  return {
    trade_number: t.trade_num ?? i + 1,
    direction,
    date: entryDate,
    entry_time: t.entry_time || null,
    entry_price: t.entry_price ?? null,
    exit_time: t.exit_time || null,
    exit_price: t.exit_price ?? null,
    duration: t.duration_human ?? null,
    pnl: t.pnl_points ?? null,
    pnl_pct: t.pnl_pct ?? null,
    // Excursion fields matching Sample_Data format
    days: weekdayName(t.entry_time),
    tday_diff: t.tday_diff ?? 0,
    average: t.average ?? t.avg_pnl_pct ?? null,
    drawdown: exc.drawdown_pct ?? null,
    lowest_point: exc.lowest_point ?? exc.low_price ?? null,
    drawdown_time: exc.drawdown_time ?? exc.low_time ?? null,
    runup: exc.runup_pct ?? null,
    highest_point: exc.highest_point ?? exc.high_price ?? null,
    highest_point_time: exc.runup_time ?? exc.high_time ?? null,
    companions: Object.fromEntries(
      Object.entries(t.companions || {}).map(([k, v]) => [k, companionToString(v)])
    ),
  };
}

// ─────────────────────────────────────────────────────────────
// Composite config builder from selected features
// ─────────────────────────────────────────────────────────────
export function buildCompositeConfig({ triggerSignal, exitSignal, selectedFeatures, asset, timeframe, session, direction, timeRestriction, backtestEnd, forwardEnd }) {
  const triggerObj = { name: triggerSignal, timeframe, trading_hour: session };
  const exitObj = { name: exitSignal, timeframe, trading_hour: session };

  const companionFeatures = selectedFeatures.filter(f => f.category === 'companion' && f.conditionType === 'time_bound');
  const beforeFeatures = companionFeatures.filter(f => f.id.endsWith('_before'));
  const temporalFeatures = selectedFeatures.filter(f => f.category === 'temporal' && f.conditionType === 'active_days');

  const subStrategies = [];
  const confirmationIds = [];

  if (beforeFeatures.length > 0) {
    const orConditions = beforeFeatures.map(f => ({
      type: 'time_bound', max_candles: 60, min_candles: 0,
      early_signal: { name: f.signal, timeframe, trading_hour: session },
      confirmation_signal: triggerObj,
    }));
    const stratId = beforeFeatures.length === 1
      ? `C1-${beforeFeatures[0].signal}-GATE`
      : `C1-COMPANION-OR-GROUP`;
    subStrategies.push({
      condition: beforeFeatures.length === 1 ? orConditions[0] : { type: 'or_group', conditions: orConditions },
      strategy_id: stratId,
    });
    confirmationIds.push(stratId);
  }

  const indicatorFeatures = selectedFeatures.filter(f => f.category === 'indicator');
  if (indicatorFeatures.length > 0) {
    const filters = indicatorFeatures.map(f => f.indicatorFilter).filter(Boolean);
    subStrategies.push({
      condition: { type: 'indicator_filter', filters, timeframe, trading_session: session },
      strategy_id: 'INDICATOR-GATE',
    });
    confirmationIds.push('INDICATOR-GATE');
  }

  const selectedDays = temporalFeatures.map(f => f.id.replace('dow_', ''));

  return {
    symbol: asset, direction, is_active: true, timeframe,
    active_days: selectedDays.length > 0 ? selectedDays : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    description: `Auto-generated composite strategy. Backtest: ${backtestEnd} | Forward: ${forwardEnd}`,
    trading_enabled: true,
    bulk_strategy_id: `${asset} ${triggerSignal} Composite — Auto`,
    time_restriction: timeRestriction,
    precedence_window_seconds: 5,
    variants: [{
      label: `${triggerSignal} Composite (Auto-Generated)`,
      priority: 1, variant_id: 'Part-A',
      exit_signal: exitObj, trading_hour: session, trigger_signal: triggerSignal,
      sub_strategies: subStrategies, required_strategies: [],
      confirmation_strategies: confirmationIds,
      confirmation_time_window_seconds: 5, signal_delivery_tolerance_seconds: 30,
    }],
    _meta: {
      generated_at: new Date().toISOString(), backtest_cutoff: backtestEnd,
      forward_end: forwardEnd, selected_features: selectedFeatures.map(f => f.id),
    },
  };
}

// ─────────────────────────────────────────────────────────────
// Signal pool helpers (for random companion / filter sampling)
// ─────────────────────────────────────────────────────────────
export function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Category-diverse sampler: round-robin across categories. */
export function sampleDiverse(pool, n) {
  if (n >= pool.length) return shuffle(pool).map(s => s.name);
  const byCat = {};
  for (const s of pool) {
    const c = s.category || 'OTHER';
    (byCat[c] = byCat[c] || []).push(s);
  }
  for (const c of Object.keys(byCat)) byCat[c] = shuffle(byCat[c]);
  const catOrder = shuffle(Object.keys(byCat));
  const out = [];
  let round = 0;
  while (out.length < n) {
    let progressed = false;
    for (const c of catOrder) {
      if (out.length >= n) break;
      const item = byCat[c][round];
      if (item) { out.push(item); progressed = true; }
    }
    if (!progressed) break;
    round++;
  }
  return out.map(s => s.name);
}
